<template>
  <div>
    <a-form :form="form" @submit="handleSubmit">
      <a-form-item :label="$t('mfa.verificationCode')">
        <a-input
          v-decorator="[
            'code',
            {
              rules: [
                {
                  required: true,
                  message: $t('mfa.codeRequired'),
                },
                {
                  validator: validateCode,
                },
              ],
            },
          ]"
          :placeholder="
            codeType === 'backup'
              ? $t('mfa.enterBackupCode')
              : $t('mfa.enterCode')
          "
          :maxLength="codeType === 'backup' ? 8 : 6"
          size="large"
        />
        <!-- Small text link to switch to backup code (only for app method) -->
        <div v-if="mfaMethod === 'app'" class="code-switch-link">
          <a
            v-if="codeType === 'totp'"
            type="link"
            @click="switchToBackupCode"
            class="switch-link"
          >
            {{ $t("mfa.useBackupCode") }}
          </a>
          <a v-else type="link" @click="switchToTOTP" class="switch-link">
            {{ $t("mfa.useAuthenticatorCode") }}
          </a>
        </div>
      </a-form-item>

      <a-form-item v-if="mfaMethod === 'sms'">
        <a-button type="link" @click="handleResend" :loading="resending">
          {{ $t("mfa.resendCode") }}
        </a-button>
      </a-form-item>

      <a-form-item v-if="mfaMethod === 'app' && codeType === 'backup'">
        <a-alert
          :message="$t('mfa.backupCodeHint')"
          type="info"
          show-icon
          class="mb-2"
        />
      </a-form-item>

      <a-form-item>
        <a-button
          type="primary"
          html-type="submit"
          size="large"
          :loading="loading"
          class="w-100"
        >
          {{ $t("mfa.verify") }}
        </a-button>
      </a-form-item>
    </a-form>
  </div>
</template>

<script>
import * as jwt from "@/services/jwt";
import { mapActions } from "vuex";

export default {
  name: "MFAVerify",
  props: {
    mfaToken: {
      type: String,
      required: true,
    },
    mfaMethod: {
      type: String,
      required: true,
      validator: (value) => ["app", "sms"].includes(value),
    },
  },
  data() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      resending: false,
      codeType: "totp", // 'totp' or 'backup'
    };
  },
  methods: {
    ...mapActions("user", ["LOGIN"]),
    validateCode(rule, value, callback) {
      if (!value) {
        callback();
        return;
      }

      if (this.codeType === "backup") {
        // Backup codes are 8 characters (alphanumeric)
        if (value.length !== 8) {
          callback(this.$t("mfa.backupCodeLength"));
          return;
        }
        // Check if it's alphanumeric (uppercase)
        if (!/^[A-Z0-9]{8}$/.test(value.toUpperCase())) {
          callback(this.$t("mfa.backupCodeFormat"));
          return;
        }
      } else {
        // TOTP codes are 6 digits
        if (value.length !== 6) {
          callback(this.$t("mfa.codeLength"));
          return;
        }
        if (!/^\d{6}$/.test(value)) {
          callback(this.$t("mfa.codeFormat"));
          return;
        }
      }
      callback();
    },
    switchToBackupCode() {
      this.codeType = "backup";
      this.form.setFieldsValue({ code: "" });
      this.form.validateFields(["code"], { force: true });
    },
    switchToTOTP() {
      this.codeType = "totp";
      this.form.setFieldsValue({ code: "" });
      this.form.validateFields(["code"], { force: true });
    },
    handleSubmit(e) {
      e.preventDefault();
      this.form.validateFields(async (err, values) => {
        if (!err) {
          this.loading = true;
          try {
            // Convert backup code to uppercase
            const code =
              this.codeType === "backup"
                ? values.code.toUpperCase()
                : values.code;
            const result = await jwt.verifyMFA(this.mfaToken, code);
            if (result && result.accessToken) {
              this.$emit("verified", result);
            }
          } catch (error) {
            // Error notification is handled in jwt service
          } finally {
            this.loading = false;
          }
        }
      });
    },
    async handleResend() {
      this.resending = true;
      try {
        await jwt.resendSMSOTP(this.mfaToken);
        this.$notification.success({
          message: this.$t("mfa.codeSent"),
          description: this.$t("mfa.codeSentDescription"),
        });
      } catch (error) {
        // Error notification is handled in jwt service
      } finally {
        this.resending = false;
      }
    },
  },
};
</script>

<style scoped>
.w-100 {
  width: 100%;
}

.code-switch-link {
  margin-top: 8px;
  text-align: right;
}

.switch-link {
  font-size: 13px;
  color: #1890ff;
  cursor: pointer;
}

.switch-link:hover {
  color: #40a9ff;
  text-decoration: underline;
}
</style>
